// The couriers a company ships with. Each is a provider of the platform
// (commerce/lib/shipping-providers on the API): the ways it delivers — to the
// door, to an office, to a locker — switched on or off for this company, and
// the company's own account with it. A company has none until it adds one
// ("Add provider", from the platform's list); with none delivering — none left,
// or every one off — its physical products are paused on the store until it
// adds one, and taking away the last one asks first.
import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Modal from "aldoo-ra/Modal"
import Tabs from "aldoo-ra/Tabs"
import SectionPicker from "./SectionPicker"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import Tooltip from "aldoo-ra/Tooltip"
import { TruckIcon, BuildingStorefrontIcon, ArchiveBoxIcon, PlusIcon, TrashIcon, Cog6ToothIcon, XMarkIcon, LinkIcon, ArrowTopRightOnSquareIcon, ArrowPathIcon, ShoppingBagIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { MapPinIcon, CheckIcon } from "@heroicons/react/24/outline"
import Spinner from "aldoo-ra/Spinner"
import { ConnectCourier, ShippingTowns } from "../AdminShared/api"
import CountriesSelect, { countryName } from "../AdminShared/CountriesSelect"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { Field, TextInput, Toggle, Section, Grid, Chip, Empty } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY, ORDERS } from "../Vendor/texts/bundles"
import { SenderPicker } from "./ShipmentDialog"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { providerSettingError } from "aldoo-ra/Store/contact-validation"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

const METHOD_ICONS = { address: TruckIcon, office: BuildingStorefrontIcon, locker: ArchiveBoxIcon, pickup: ShoppingBagIcon }
// whether Aldoo took a courier off (it delivers nothing), or takes it off on a date (it delivers until then)
const platformOff = (provider) => provider?.availability?.status === "disabled"
const platformEnds = (provider) => (provider?.availability?.status === "scheduled" ? provider.availability.disableAt : null)
// a way in a word, in the courier dialog's cards
const WAY_NAMES = { address: "Address", office: "Office", locker: "Locker", pickup: "Shop" }

// a courier of the platform's on, with a way of it on (a way not stored is on): what delivers the
// company's physical products — the API's rule (vendors/lib/sales-pauses shipsWithProvider)
const deliversWith = (catalog) => (list) =>
  (Array.isArray(list) ? list : []).some((entry) => {
    const provider = catalog.find((item) => item.id === entry?.providerId)
    return !!provider && !platformOff(provider) && entry.enabled !== false && provider.methods.some((method) => entry.methods?.[method.id]?.enabled !== false)
  })

/**
 * Whether a setting fails its field's `format` (the provider says it: Speedy's
 * client and office ids are digits, its service a whole number above zero) —
 * the rule profile_save applies (aldoo-ra/Store/contact-validation).
 */
const settingFails = (field, value) => value !== undefined && value !== null && value !== "" && !!providerSettingError(field.format, value)

// a way's own price list (shipping-providers on the API): lines up to a weight, at a price
const MAX_PRICE_LINES = 12
const MAX_LINE_KG = 100
const amountOf = (value) => {
  const amount = Number(String(value ?? "").replace(",", "."))
  return Number.isFinite(amount) ? amount : NaN
}
const maxKgOf = (method) => (method?.maxWeightGrams ? method.maxWeightGrams / 1000 : MAX_LINE_KG)

/** Why a line of a way's price list is not one the API takes ("weight", "price", "twice"), or null. */
const lineProblem = (line, method, lines) => {
  const kg = amountOf(line?.upToKg)
  if (!(kg > 0) || kg > maxKgOf(method)) return "weight"
  // 0 is free delivery (the company's own terms); an empty price is not 0
  const price = String(line?.price ?? "").trim() === "" ? NaN : amountOf(line?.price)
  if (!(price >= 0) || price > 10000) return "price"
  if (lines.filter((other) => amountOf(other?.upToKg) === kg).length > 1) return "twice"
  return null
}
const pricesFail = (lines, method) => Array.isArray(lines) && (lines.length > MAX_PRICE_LINES || lines.some((line) => lineProblem(line, method, lines)))

/** Whether any provider in the list has a setting its field does not take, or a price list the API would not. */
export const invalidProviderSetting = (list, catalog = []) =>
  (Array.isArray(list) ? list : []).some((entry) => {
    const provider = catalog.find((known) => known.id === entry?.providerId)
    if (!provider) return false
    if (provider.methods.some((method) => pricesFail(entry.methods?.[method.id]?.prices, method))) return true
    if (provider.methods.some((method) => (entry.methods?.[method.id]?.locations || []).some((place) => pricesFail(place?.prices, method)))) return true
    return provider.configFields.some((field) => settingFails(field, entry.settings?.[field.key]))
  })

const money = (value) => `€${Number(value).toFixed(2)}`

// a town as the API compares it (shipping-providers townKey): "гр. СОФИЯ", "София", "Sofia" are one
const LATIN = { а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sht", ъ: "a", ь: "y", ю: "yu", я: "ya" }
export const townKey = (value) =>
  [...String(value || "").toLowerCase().replace(/\[[^\]]*\]|\d+/g, " ").replace(/^\s*(гр|град|с|село|gr|grad|s|selo)\.?\s+/u, "").replace(/[^\p{L}\s-]/gu, " ").trim().replace(/\s+/g, " ")]
    .map((ch) => LATIN[ch] ?? ch)
    .join("")
    .replace(/y/g, "i")
    .replace(/i+/g, "i")
const ANY = "__any__"

/** A price list's lines, editable: up to a weight, at a price; `onChange(lines | undefined, options)`. */
function PriceLines({ title, method, lines, canEdit, onChange, onFlush, onReset, resetLabel, testId }) {
  const t = usePortalTexts(COMPANY)
  const setLine = (index, key, value) => onChange(lines.map((line, i) => (i === index ? { ...line, [key]: value } : line)))
  const removeLine = (index) => (lines.length > 1 ? onChange(lines.filter((_, i) => i !== index), { now: true }) : onReset())
  const addLine = () => {
    const heaviest = Math.max(0, ...lines.map((line) => amountOf(line.upToKg) || 0))
    onChange([...lines, { upToKg: Math.min(maxKgOf(method), heaviest ? heaviest * 2 : 1), price: "" }])
  }
  const PROBLEMS = { weight: t("A weight between 0 and %s kg", maxKgOf(method)), price: t`A price, 0 for free delivery, e.g. 3.20`, twice: t`Each weight once` }
  return (
    <div className="flex-1 flex flex-col gap-1.5">
      {/* the list's title, a line added with the + beside it */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-admin_text">{title}</span>
        {canEdit && lines.length < MAX_PRICE_LINES && (
          <button type="button" onClick={addLine} className="p-0.5 rounded-full text-admin_primary hover:bg-admin_primary/10" title={t`Add line`} aria-label={t`Add line`} data-testid={`${testId}-add`}>
            <PlusIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      {lines.map((line, index) => {
        const problem = lineProblem(line, method, lines)
        return (
          <div key={index} className="flex flex-col gap-0.5">
            <div className="flex items-center gap-1.5 text-xs text-admin_text_secondary">
              <span>{t`up to`}</span>
              <TextInput
                inputMode="decimal"
                className={`h-8 w-16 px-2 text-xs ${problem === "weight" || problem === "twice" ? "border-admin_error" : ""}`}
                value={line.upToKg ?? ""}
                disabled={!canEdit}
                onChange={(value) => /^\d*[.,]?\d{0,3}$/.test(value) && setLine(index, "upToKg", value)}
                onBlur={onFlush}
                aria-label={t`Up to kg`}
              />
              <span>{t`kg`}</span>
              <span className="ml-auto">€</span>
              <TextInput
                inputMode="decimal"
                className={`h-8 w-20 px-2 text-xs tabular-nums ${problem === "price" ? "border-admin_error" : ""}`}
                value={line.price ?? ""}
                disabled={!canEdit}
                placeholder="3.20"
                onChange={(value) => /^\d*[.,]?\d{0,2}$/.test(value) && setLine(index, "price", value)}
                onBlur={onFlush}
                aria-label={t`Price (EUR)`}
              />
              {canEdit && (
                <button type="button" onClick={() => removeLine(index)} className="p-1 rounded text-admin_text_secondary hover:text-admin_error" title={t`Remove line`}>
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            {problem && <span className="text-[11px] text-admin_error">{PROBLEMS[problem]}</span>}
          </div>
        )
      })}
      {canEdit && resetLabel && (
        <div className="mt-auto pt-2 flex justify-center">
          <Button variant="admin_secondary" className="h-8 text-xs" onClick={onReset} data-testid={testId}>
            {resetLabel}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * What a buyer pays for one parcel by a way. "Prices for" picks where: Any
 * location — the platform's price list until the company sets its own (its
 * contract with the courier) — or a town of its own, with a price list for
 * parcels going there (to an address in it, to an office or locker in it).
 * A town is found in the couriers' towns or typed; it starts from the prices
 * for anywhere; "Remove location" leaves it to them. A parcel heavier than
 * the last line is priced the platform's way.
 * `way` { prices?, locations? }; `onChange(patch, options)` — a key set to undefined is dropped.
 */
function MethodPrices({ method, way = {}, canEdit, onChange, onFlush, towns = [] }) {
  const t = usePortalTexts(COMPANY)
  const platform = Array.isArray(method.platformPrices) ? method.platformPrices : []
  const own = Array.isArray(way.prices) ? way.prices : null
  const places = Array.isArray(way.locations) ? way.locations : []
  const [where, setWhere] = useState(ANY)
  const place = where === ANY ? null : places.find((item) => townKey(item.city) === where) || null
  // a town picked but removed meanwhile (or undone): back to anywhere
  useEffect(() => {
    if (where !== ANY && !place) setWhere(ANY)
  }, [where, place])

  const setPlaces = (next, options) => onChange({ locations: next.length ? next : undefined }, options)
  const startLines = () => (own || platform).map((line) => ({ upToKg: line.upToKg, price: line.price }))
  const addPlace = (city) => {
    const name = String(city || "").trim()
    if (!name) return
    const key = townKey(name)
    if (!places.some((item) => townKey(item.city) === key)) {
      const lines = startLines()
      setPlaces([...places, { country: "BG", city: name, prices: lines.length ? lines : [{ upToKg: maxKgOf(method), price: "" }] }], { now: lines.length > 0 })
    }
    setWhere(key)
  }
  const setPlaceLines = (lines, options) =>
    lines === undefined ? removePlace() : setPlaces(places.map((item) => (item === place ? { ...item, prices: lines } : item)), options)
  const removePlace = () => {
    setPlaces(places.filter((item) => item !== place), { now: true })
    setWhere(ANY)
  }

  // back to the courier's prices, from the card's reset button: this location (any location: the
  // company's list goes; a town: its list is the courier's lines again) or every location of the way —
  // each asked again before it is done
  const courierLines = () => (platform.length ? platform.map((line) => ({ upToKg: line.upToKg, price: line.price })) : null)
  const resetWhere = (key) => {
    const town = key === ANY ? null : places.find((item) => townKey(item.city) === key)
    showPromptDialog({
      title: t`Reset the prices?`,
      message: town ? t("The prices for %s go back to the courier's.", town.city) : t`Your prices for any location go back to the courier's.`,
      confirmText: t`Reset`,
      cancelText: t`Cancel`,
      onConfirm: () => {
        if (!town) return onChange({ prices: undefined }, { now: true })
        const lines = courierLines()
        if (!lines) return removePlace()
        setPlaces(places.map((item) => (item === town ? { ...item, prices: lines } : item)), { now: true })
      },
    })
  }
  const resetAll = () =>
    showPromptDialog({
      title: t`Reset all the prices?`,
      message: t`Every price you set for this delivery way goes back to the courier's.`,
      confirmText: t`Reset all`,
      cancelText: t`Cancel`,
      onConfirm: () => {
        setWhere(ANY)
        onChange({ prices: undefined, locations: undefined }, { now: true })
      },
    })
  const [resetting, setResetting] = useState(false)
  const customized = !!own || places.length > 0
  const hereName = place ? place.city : t`Any location`

  const used = new Set(places.map((item) => townKey(item.city)))
  const options = [
    { value: ANY, name: t`Any location`, kind: "any" },
    ...places.map((item) => ({ value: townKey(item.city), name: item.city, kind: "place" })),
    ...towns.filter((town) => !used.has(townKey(town.city))).map((town) => ({ value: townKey(town.city), name: town.city, kind: "town" })),
  ]
  const selected = options.find((option) => option.value === where) || options[0]

  return (
    <div className="flex-1 flex flex-col gap-4 border-t border-admin_bordered/60 pt-3" data-testid={`shipping-prices-${method.id}`}>
      {/* reset: at the card's top right, there whatever is shown below */}
      {/* placed by the wrapper (the tooltip's own box is relative); the tooltip only while there is
          something to reset — on the courier's prices already, the button is dimmed and says nothing */}
      {canEdit && (() => {
        const reset = (
          <button
            type="button"
            onClick={() => setResetting(true)}
            disabled={!customized}
            className="flex p-1.5 rounded-full text-admin_text_secondary hover:text-admin_primary hover:bg-admin_primary/10 disabled:opacity-30 disabled:pointer-events-none"
            aria-label={t`Use courier prices`}
            data-testid={`shipping-prices-reset-${method.id}`}
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        )
        return (
          <span className="absolute top-2.5 right-2.5 flex">
            {customized ? <Tooltip text={t`Use courier prices`} position="top" variant="admin">{reset}</Tooltip> : reset}
          </span>
        )
      })()}
      <Modal isOpen={resetting} onClose={() => setResetting(false)} variant="admin_compact_fit" closeOnEscape title={t`Use courier prices`}>
        <div className="flex flex-col gap-3 p-5 pt-1 w-[min(92vw,420px)]" data-testid={`shipping-prices-reset-dialog-${method.id}`}>
          <span className="text-sm text-admin_text_secondary">{t`Back to the courier's prices and weights:`}</span>
          <Button
            variant="admin_secondary"
            className="w-full justify-start"
            disabled={place ? false : !own}
            onClick={() => {
              setResetting(false)
              resetWhere(place ? where : ANY)
            }}
            data-testid={`shipping-prices-reset-here-${method.id}`}
          >
            {t("This location (%s)", hereName)}
          </Button>
          <Button
            variant="admin_secondary"
            className="w-full justify-start"
            onClick={() => {
              setResetting(false)
              resetAll()
            }}
            data-testid={`shipping-prices-reset-all-${method.id}`}
          >
            {t`All locations`}
          </Button>
        </div>
      </Modal>
      {/* the label over the control: the control the card's whole width, its name cut short when long */}
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-xs font-semibold text-admin_text">{t`Prices for`}</span>
        <div className="w-full min-w-0">
          <DropdownSelect
            className="w-full"
            variant="admin"
            options={options}
            value={[selected]}
            searchEnabled
            searchField="name"
            creatable={canEdit}
            onCreate={(query) => addPlace(query)}
            createLabel={(query) => t("Add “%s”", query)}
            showCheckbox={false}
            showSelectionTick
            enableResetButton={false}
            allowEmptySelection={false}
            dropdownWidth="260px"
            maxHeight="320px"
            onItemRender={(option) => (
              <span className="flex items-center gap-2 w-full min-w-0 text-sm">
                {option.kind === "any" ? <TruckIcon className="w-4 h-4 shrink-0" /> : <MapPinIcon className="w-4 h-4 shrink-0" />}
                <span className="flex-1 min-w-0 truncate">{option.name}</span>
                {(option.kind === "place" || (option.kind === "any" && own)) && <span className="text-[10px] font-semibold uppercase text-admin_success">{t`own`}</span>}
              </span>
            )}
            onChange={(picked) => {
              const option = Array.isArray(picked) ? picked[0] : picked
              if (!option) return
              if (option.kind === "town") return canEdit && addPlace(option.name)
              setWhere(option.value)
            }}
            data-testid={`shipping-prices-where-${method.id}`}
          />
        </div>
      </div>

      {place ? (
        <>
          <PriceLines
            title={t("Your prices in %s", place.city)}
            method={method}
            lines={place.prices || []}
            canEdit={canEdit}
            onChange={setPlaceLines}
            onFlush={onFlush}
            onReset={removePlace}
            resetLabel={t`Remove location`}
            testId={`shipping-prices-remove-place-${method.id}`}
          />
        </>
      ) : own ? (
        <>
          <PriceLines
            title={t`Your prices`}
            method={method}
            lines={own}
            canEdit={canEdit}
            onChange={(lines, options) => onChange({ prices: lines }, options)}
            onFlush={onFlush}
            onReset={() => resetWhere(ANY)}
            testId={`shipping-prices-own-lines-${method.id}`}
          />
        </>
      ) : (
        <div className="flex-1 flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-admin_text">{t`Provider prices`}</span>
          {platform.length ? (
            <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
              {platform.map((line) => (
                <li key={line.upToKg} className="flex justify-between text-xs text-admin_text_secondary">
                  <span>{t("Up to %s kg", line.upToKg)}</span>
                  <span className="tabular-nums">{money(line.price)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-xs text-admin_text_secondary">{t`Priced by the courier at checkout`}</span>
          )}
          {canEdit && (
            <Button
              variant="admin_secondary"
              className="h-8 text-xs self-center mt-auto"
              onClick={() => onChange({ prices: platform.length ? platform.map((line) => ({ ...line })) : [{ upToKg: maxKgOf(method), price: "" }] }, { now: platform.length > 0 })}
              data-testid={`shipping-prices-own-${method.id}`}
            >
              {t`Set your own prices`}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * How a way is priced, in a line for the provider's card: the provider's
 * prices (or the courier's live ones, with the company's account), or the
 * company's own — from the lightest line's price — and its towns'.
 */
function wayPlan(t, provider, method, way = {}, connected) {
  // pickup from the company's shop: free unless it set a price
  if (provider.id === "shop") {
    const price = Number(way.prices?.[0]?.price) || 0
    return price > 0 ? { text: t("Pickup %s", money(price)), own: true } : { text: t`Free pickup`, own: false }
  }
  const fromOf = (lines) => {
    const prices = (lines || []).map((line) => Number(line.price)).filter((price) => price >= 0)
    if (!prices.length) return null
    // every line 0: the company delivers that way for free
    if (prices.every((price) => price === 0)) return t`Free delivery`
    const low = Math.min(...prices)
    return lines.length === 1 ? t("%s up to %s kg", money(low), lines[0].upToKg) : t("from %s", money(low))
  }
  const own = fromOf(way.prices)
  const base = own ? t("Your prices %s", own) : connected ? t("%s prices", provider.name) : t`Platform prices`
  const places = (way.locations || []).map((item) => `${item.city} ${fromOf(item.prices) || ""}`.trim())
  if (!places.length) return { text: base, own: !!own }
  const shown = places.slice(0, 3).join(", ") + (places.length > 3 ? ` +${places.length - 3}` : "")
  return { text: `${t("Elsewhere: %s", base)} · ${shown}`, own: true }
}

/** A provider as a company gets it when it adds it: every way on, nothing filled in. */
const entryOf = (provider) => ({
  providerId: provider.id,
  enabled: true,
  methods: Object.fromEntries(provider.methods.map((method) => [method.id, { enabled: true }])),
  settings: {},
})

// the courier's logo from the CMS (ShippingCarrier, on the catalog as `carrier`), so a vendor
// knows it at a glance; a provider without one shows the truck
export function ProviderLogo({ provider, className = "", off = false }) {
  const src = provider?.carrier?.logo
  // the company's own shop is no courier: a shop front, not a truck
  if (!src && provider?.id === "shop") return <BuildingStorefrontIcon className={`${className} ${off ? "text-admin_text_secondary" : "text-admin_primary"}`} />
  if (!src) return <TruckIcon className={`${className} ${off ? "text-admin_text_secondary" : "text-admin_primary"}`} />
  return <img src={cmsUrl(src)} alt="" className={`${className} object-contain ${off ? "grayscale opacity-60" : ""}`} />
}

// the account fields (the courier's API user and password: `group` "account" on the API) and the rest (the sender's)
// (an API without the groups yet: the user and password are the account)
const isAccountField = (field) => field.group === "account" || (!field.group && ["userName", "password", "apiKey", "apiSecret", "oauthClientId", "oauthClientSecret"].includes(field.key))
const accountFields = (provider) => (provider.configFields || []).filter(isAccountField)
// (no drop-off office: the courier takes a parcel at any office — an older API's field is left out)
const senderFields = (provider) => (provider.configFields || []).filter((field) => !isAccountField(field) && !["dropoffOfficeId", "senderOfficeCode"].includes(field.key))
// a field chosen from the connected account's list rather than typed (Speedy's sender client: `picker` "sender")
const isSenderPick = (provider, field) => field.picker === "sender" || (!field.picker && ["speedy", "pigeon", "boxnow"].includes(provider.id) && field.key === "clientId")
// the service, from the account's list (`picker` "service")
const isServicePick = (provider, field) => field.picker === "service" || (!field.picker && provider.id === "speedy" && field.key === "serviceId")
// Speedy names its services in capitals ("СТАНДАРТ 24 ЧАСА"): the known ones by their own names, with what
// each is in a sentence; another Cyrillic name read as a sentence, a Latin one as Speedy writes it
const SPEEDY_SERVICES = (t) => ({
  505: { name: t`Standard 24 hours`, about: t`Delivery within Bulgaria by the next working day.` },
  202: { name: "Speedy CEE Economy", about: t`Economy delivery to countries in Central and Eastern Europe.` },
  302: { name: "Air Express", about: t`Fast air delivery abroad.` },
  306: { name: "DPD Economy", about: t`Economy delivery to EU countries through the DPD network.` },
  308: { name: "DPD Economy CR", about: t`Economy delivery to EU countries through the DPD network.` },
})
const serviceName = (text) => {
  const value = String(text || "")
  if (!/[А-Яа-я]/.test(value)) return value
  const lower = value.toLocaleLowerCase()
  return lower.charAt(0).toLocaleUpperCase() + lower.slice(1)
}
/** Whether the company's own account with the courier is in (its user and password saved). */
// (an `optional` account field — BOX NOW's own API address — is not needed to be connected)
const isConnected = (provider, entry) => accountFields(provider).length > 0 && accountFields(provider).every((field) => field.optional || entry?.settings?.[field.key])

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-admin_primary underline">
    {children}
    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
  </a>
)

/**
 * How a company gets its API access with the courier, step by step: Speedy issues an API user and password
 * of its own on request (not the MySpeedy login); Econt's API takes the company's e-Econt account.
 */
function ConnectGuide({ provider }) {
  const t = usePortalTexts(COMPANY)
  const steps =
    provider.id === "speedy"
      ? [
          <>{t`You need a company account with Speedy. If you have none, register at`} <Ext href="https://myspeedy.speedy.bg">myspeedy.speedy.bg</Ext></>,
          <>{t`Ask Speedy for API access for your online shop: send the request on Speedy's online shop integration page, or ask your Speedy sales representative.`} <Ext href="https://www.speedy.bg/bg/online-shop-integration">{t`Online shop integration`}</Ext></>,
          t`Speedy emails you a user name (digits) and a password for the API. They are not your MySpeedy login.`,
          t`Enter them here and press Connect.`,
        ]
      : provider.id === "econt"
        ? [
            <>{t`You need a company account in e-Econt. If you have none, register at`} <Ext href="https://ee.econt.com">ee.econt.com</Ext></>,
            t`To collect cash on delivery, your account needs an agreement with Econt: ask your Econt office or sales representative.`,
            t`Enter your e-Econt user name and password here and press Connect.`,
          ]
        : provider.id === "pigeon"
          ? [
              <>{t`You need a business account with Pigeon Express. If you have none, register as a business client at`} <Ext href="https://pigeonexpress.com/api-integration">pigeonexpress.com</Ext></>,
              t`Sign in to your Pigeon Express account and open its API keys: copy the API key and the API secret.`,
              t`Enter them here and press Connect. Your saved addresses in Pigeon Express become the places your parcels go from.`,
            ]
          : provider.id === "boxnow"
            ? [
                t`Ask BOX NOW for access to its Partner API: email integrationsupport@boxnow.bg with your company's name, address, tax id and contact details, the phone numbers that will sign in to the Partner Portal, and the addresses BOX NOW collects your parcels from.`,
                t`BOX NOW sends you a client id (OAUTH_CLIENT_ID), a client secret (OAUTH_CLIENT_SECRET) and your API address.`,
                t`Enter the client id and the secret here and press Connect. Enter the API address only when it is not the one shown.`,
              ]
            : provider.id === "direx"
              ? [
                  <>{t`You need a business account with Direx. If you have none, register at`} <Ext href="https://my.direx.bg">my.direx.bg</Ext></>,
                  t`In my.direx.bg, open Settings → API → Create. Enter a name for the keys (e.g. Aldoo) and https://aldoo.com as the site's address, then press Save: Direx makes a client id (ID) and a client secret (Secret).`,
                  t`Add the sender your parcels go from in my.direx.bg (Senders), if it is not there yet.`,
                  t`Enter the client id and the secret here and press Connect, then choose the sender and the product for parcels to an address.`,
                ]
              : []
  if (!steps.length) return null
  return (
    <div className="rounded-lg border border-admin_bordered bg-admin_canvas px-4 py-3 flex flex-col gap-2" data-testid={`connect-guide-${provider.id}`}>
      <div className="text-sm font-semibold text-admin_text">{t("How to get your %s API access", provider.name)}</div>
      <ol className="m-0 pl-5 list-decimal flex flex-col gap-1.5 text-sm text-admin_text_secondary">
        {steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {provider.id === "direx" && (
        <div className="text-xs text-admin_text_secondary">
          {t`Direx collects your parcels once you close them out in my.direx.bg (a manifest).`} <Ext href="https://api.direx.bg/docs/">api.direx.bg/docs</Ext>
        </div>
      )}
      {provider.id === "speedy" && (
        <div className="text-xs text-admin_text_secondary">
          {t`To send parcels from Speedy lockers, also complete Speedy's identification:`} <Ext href="https://myspeedy.speedy.bg/verification">myspeedy.speedy.bg/verification</Ext>
        </div>
      )}
    </div>
  )
}

/**
 * "Connect to your Speedy account": the API user and password, checked with the courier (vendor
 * courier_connect) — a loader while it answers, then that it is connected, or what went wrong.
 * `onConnected(providers)`: the company's list as the API now has it.
 */
// `sender`: the office parcels go from (Speedy's client); `onSender(id)` keeps the one picked. `askLocation`
// false: the location is not asked here (the waybill asks it), and the last button says `doneLabel`
export function ConnectDialog({ orgId, provider, sender, onClose, onConnected, onSender, askLocation = true, doneLabel = "" }) {
  const t = usePortalTexts(COMPANY)
  const to = usePortalTexts(ORDERS)
  const [picking, setPicking] = useState(false)
  const [picked, setPicked] = useState(null)
  const fields = accountFields(provider)
  const [values, setValues] = useState({})
  const [phase, setPhase] = useState("form") // form | connecting | done | failed
  const [answer, setAnswer] = useState(null)
  const ready = fields.every((field) => field.optional || String(values[field.key] || "").trim())
  const connect = async () => {
    setPhase("connecting")
    // the courier's own account fields (Speedy, Econt: user and password; Pigeon Express: API key and secret)
    const account = Object.fromEntries(fields.map((field) => [field.key, values[field.key] || ""]))
    const res = await ConnectCourier(orgId, provider.id, { account }).catch((e) => ({ error: "courier_unreachable", message: e.message }))
    setAnswer(res)
    if (res?.result) {
      onConnected?.(res.providers)
      return setPhase("done")
    }
    setPhase("failed")
  }
  const reason = (res) =>
    res?.error === "invalid_credentials"
      ? fields.some((field) => field.key === "oauthClientId")
        ? t("%s did not accept this client id and secret. Check them in the email from %s and try again.", provider.name, provider.name)
        : fields.some((field) => field.key === "apiKey")
        ? t("%s did not accept this API key and secret. Check them in your %s account and try again.", provider.name, provider.name)
        : t("%s did not accept this user name and password. Check them in the email from %s and try again.", provider.name, provider.name)
      : res?.error === "missing_credentials"
        ? t`Enter the user name and the password.`
        : t("%s could not be reached right now. Try again in a few minutes.", provider.name)
  return (
    <Modal
      isOpen
      onClose={phase === "connecting" ? () => {} : onClose}
      variant="admin"
      closeOnEscape={phase !== "connecting"}
      allowCloseBtn={phase !== "connecting"}
      title={phase === "form" || phase === "connecting" ? t("Connect to your %s account", provider.name) : ""}
      styleOverrides={{ container: "w-[min(94vw,560px)] max-h-[90vh] overflow-y-auto bg-admin_paper rounded-lg border border-admin_bordered shadow-lg" }}
    >
      <div className={`px-5 pb-5 pt-1 flex flex-col gap-4 ${phase === "done" || phase === "failed" ? "min-h-[min(70vh,520px)]" : "min-h-[300px]"}`} data-testid={`connect-dialog-${provider.id}`} data-phase={phase}>
        {(phase === "form" || phase === "connecting") && (
          <div className="relative flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <ProviderLogo provider={provider} className="w-16 h-9 shrink-0 rounded" />
              <span className="text-sm text-admin_text_secondary">{t("Your own account: waybills are made with it from the order, at your prices with %s.", provider.name)}</span>
            </div>
            <Grid cols={2}>
              {fields.map((field) => (
                <Field key={field.key} label={field.optional ? `${t(field.label)} (${t`optional`})` : t(field.label)} hint={field.optional && field.hint ? t(field.hint) : undefined}>
                  <TextInput
                    type={field.type === "password" ? "password" : "text"}
                    value={values[field.key] || ""}
                    placeholder={field.placeholder || ""}
                    autoComplete={field.type === "password" ? "new-password" : "off"}
                    onChange={(value) => setValues((v) => ({ ...v, [field.key]: value }))}
                    onKeyDown={(e) => e.key === "Enter" && ready && connect()}
                    disabled={phase === "connecting"}
                    data-testid={`connect-${field.key}`}
                  />
                </Field>
              ))}
            </Grid>
            <ConnectGuide provider={provider} />
            <div className="flex justify-end gap-2">
              <Button variant="admin_secondary" onClick={onClose} disabled={phase === "connecting"}>{t`Cancel`}</Button>
              <Button variant="admin_primary" onClick={connect} disabled={!ready || phase === "connecting"} data-testid="connect-submit">
                <span className="inline-flex items-center gap-2">
                  {phase === "connecting" && <Spinner variant="white" size="sm" />}
                  {phase === "connecting" ? t`Connecting…` : t`Connect`}
                </span>
              </Button>
            </div>
            {/* the courier asked: a loader over the form */}
            {phase === "connecting" && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-admin_paper/90 rounded-md" data-testid="connect-loading">
                <Spinner variant="admin_primary" size="lg" />
                <span className="text-sm text-admin_text_secondary">{t("Connecting to %s…", provider.name)}</span>
              </div>
            )}
          </div>
        )}
        {phase === "done" && (() => {
          const senders = answer?.senders || []
          // where parcels go from: the only office (set with the account), the one picked, or the one kept before
          const location = !askLocation ? null : senders.length === 1 ? senders[0] : senders.find((s) => s.id === (picked || String(sender || "")))
          const needsLocation = askLocation && senders.length > 1 && !location
          return (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center" data-testid="connect-done">
              <CheckCircleIcon className="w-20 h-20 text-admin_success" />
              <div className="text-xl font-bold">{t("You have connected your %s account", provider.name)}</div>
              {needsLocation && <p className="m-0 text-sm text-admin_text_secondary">{t("%s found %s sending addresses on your account. Choose where your parcels go from.", provider.name, senders.length)}</p>}
              {/* where parcels go from, in a box of its own: the whole box picks another office (when there are several) */}
              {location &&
                (() => {
                  const body = (
                    <>
                      <MapPinIcon className="w-6 h-6 shrink-0 text-admin_primary" />
                      <span className="flex-1 min-w-0 flex flex-col text-left">
                        <span className="text-[11px] uppercase tracking-wide text-admin_text_secondary">{t`Your parcels go from`}</span>
                        <span className="text-sm font-semibold text-admin_text">{location.address || location.name}</span>
                        {location.address && location.name && <span className="text-xs text-admin_text_secondary">{location.name}</span>}
                      </span>
                      {senders.length > 1 && <span className="shrink-0 text-sm font-semibold text-admin_primary">{t`Change`}</span>}
                    </>
                  )
                  const box = "w-full max-w-[460px] mt-2 flex items-center gap-3 rounded-lg border border-admin_bordered bg-admin_neutral_1 px-4 py-3"
                  return senders.length > 1 ? (
                    <button type="button" onClick={() => setPicking(true)} className={`${box} hover:border-admin_primary/60 hover:bg-admin_primary/5 transition-colors`} data-testid="connect-location">
                      {body}
                    </button>
                  ) : (
                    <div className={box} data-testid="connect-location">
                      {body}
                    </div>
                  )
                })()}
              {needsLocation ? (
                <Button variant="admin_primary" onClick={() => setPicking(true)} className="!h-14 !px-8 !text-lg mt-6" data-testid="connect-set-location">
                  {t`Set your location`}
                </Button>
              ) : (
                <Button variant="admin_primary" onClick={onClose} className={senders.length || doneLabel ? "!h-14 !px-8 !text-lg mt-6" : ""} data-testid="connect-close">
                  {doneLabel || (senders.length ? t`You're now connected` : t`Done`)}
                </Button>
              )}
              {picking && (
                <SenderPicker
                  senders={senders}
                  value={picked || String(sender || "")}
                  courierName={provider.name}
                  t={to}
                  onPick={(id) => {
                    setPicking(false)
                    setPicked(id)
                    onSender?.(id)
                  }}
                  onClose={() => setPicking(false)}
                />
              )}
            </div>
          )
        })()}
        {phase === "failed" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center" data-testid="connect-failed">
            <ExclamationTriangleIcon className="w-20 h-20 text-admin_warning" />
            <div className="text-xl font-bold">{t`The account could not be connected`}</div>
            <p className="m-0 max-w-[420px] text-sm text-admin_text_secondary">{reason(answer)}</p>
            <div className="flex gap-2">
              <Button variant="admin_secondary" onClick={onClose}>{t`Close`}</Button>
              <Button variant="admin_primary" onClick={() => setPhase("form")} data-testid="connect-retry">{t`Try again`}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

/**
 * Where parcels go from: picked from the offices the connected account has with the courier (the same
 * picker as the waybill's), the one chosen kept as the setting. Without the account, it asks to connect.
 */
function SenderField({ orgId, provider, field, value, connected, canEdit, onPick, onConnect }) {
  const t = usePortalTexts(COMPANY)
  const to = usePortalTexts(ORDERS)
  const [senders, setSenders] = useState(null)
  const [picking, setPicking] = useState(false)
  const [error, setError] = useState("")
  useEffect(() => {
    if (!connected || !orgId) return
    let gone = false
    ConnectCourier(orgId, provider.id, { senders: true })
      .then((res) => !gone && (res?.senders ? setSenders(res.senders) : setError(res?.message || t`The offices could not be loaded`)))
      .catch((e) => !gone && setError(e.message))
    return () => {
      gone = true
    }
  }, [connected, orgId, provider.id])
  const chosen = senders?.find((sender) => sender.id === String(value || ""))
  return (
    <Field label={t(field.label)} hint={t`The office parcels are sent from, out of those on your account.`}>
      {/* on a phone the words above the button (the box's width); from sm side by side */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 rounded-md border border-admin_bordered px-3 py-2 min-h-[46px]" data-testid={`shipping-setting-${field.key}`}>
        {!connected ? (
          <span className="flex-1 text-sm text-admin_text_secondary">{t("Connect your %s account to choose the office.", provider.name)}</span>
        ) : !senders && !error ? (
          <span className="flex-1 flex items-center gap-2 text-sm text-admin_text_secondary"><Spinner variant="admin_primary" size="sm" />{t`Loading…`}</span>
        ) : (
          <span className="flex-1 min-w-0 flex flex-col">
            <span className="text-sm font-semibold text-admin_text truncate">{chosen ? chosen.address || chosen.name : value ? value : t`Not chosen yet`}</span>
            {chosen && <span className="text-xs text-admin_text_secondary truncate">{chosen.name} · {chosen.id}</span>}
            {error && <span className="text-xs text-admin_error">{error}</span>}
          </span>
        )}
        {canEdit && (!connected ? (
          <Button variant="admin_secondary" onClick={onConnect} className="max-sm:w-full">{t`Connect`}</Button>
        ) : senders?.length > 0 && (
          <Button variant="admin_secondary" onClick={() => setPicking(true)} className="max-sm:w-full" data-testid="sender-choose">{value ? t`Change` : t`Choose`}</Button>
        ))}
      </div>
      {picking && (
        <SenderPicker
          senders={senders}
          value={String(value || "")}
          courierName={provider.name}
          t={to}
          onPick={(id) => {
            setPicking(false)
            onPick(id)
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </Field>
  )
}

/** The courier's service, by its name: the ones the connected account's contract has (Speedy's list). */
function ServiceField({ orgId, provider, field, value, connected, canEdit, onPick }) {
  const t = usePortalTexts(COMPANY)
  const language = useSiteLanguage()
  const [services, setServices] = useState(null)
  useEffect(() => {
    if (!connected || !orgId) return
    let gone = false
    ConnectCourier(orgId, provider.id, { services: true })
      .then((res) => !gone && setServices(res?.services || []))
      .catch(() => !gone && setServices([]))
    return () => {
      gone = true
    }
  }, [connected, orgId, provider.id])
  const fallback = field.placeholder ? String(field.placeholder) : ""
  // the account's services; without the account (or its list), the standard one the courier uses
  const list = services?.length ? services : fallback ? [{ id: fallback, name: "СТАНДАРТ 24 ЧАСА", nameEn: "STANDARD 24 HOURS" }] : []
  const known = provider.id === "speedy" ? SPEEDY_SERVICES(t) : {}
  const nameOf = (service) => known[service.id]?.name || serviceName(language === "bg" ? service.name : service.nameEn || service.name)
  const options = list.map((service) => ({ value: service.id, label: `${nameOf(service)}${service.id === fallback ? ` (${t`default`})` : ""}`, about: known[service.id]?.about || "", hint: service.id }))
  const current = String(value || fallback)
  const selected = options.find((o) => o.value === current) || (current ? { value: current, label: current } : null)
  return (
    <Field label={t`Service`} hint={t("The %s service your parcels are sent with.", provider.name)}>
      <div data-testid={`shipping-setting-${field.key}`}>
        <DropdownSelect
          variant="admin"
          options={options}
          value={selected ? [selected] : []}
          nameField="label"
          showCheckbox={false}
          showSelectionTick
          enableResetButton={false}
          allowEmptySelection={false}
          disabled={!canEdit}
          onChange={(sel) => {
            const picked = Array.isArray(sel) ? sel[0] : sel
            if (picked?.value) onPick(picked.value === fallback ? "" : picked.value)
          }}
          onItemRender={(o) => (
            <span className="flex flex-col py-0.5">
              <span className="text-sm text-admin_text">{o.label}</span>
              {o.about && <span className="text-xs text-admin_text_secondary">{o.about}</span>}
              <span className="text-[11px] text-admin_text_secondary/70">{o.hint}</span>
            </span>
          )}
          onValueItemRender={(o) => {
            const item = Array.isArray(o) ? o[0] : o
            return <span className="truncate text-sm">{item?.label ?? selected?.label ?? ""}</span>
          }}
        />
      </div>
    </Field>
  )
}

/** A provider's row: what it is and how it stands; its settings open in a dialog (ProviderDialog). */
// `blocked`: pickup from the shop with no shop open — shown off, switching it on asks for a shop first (`onBlocked`)
function ProviderRow({ provider, entry, canEdit, onChange, onOpen, onConnect, blocked = false, onBlocked }) {
  const t = usePortalTexts(COMPANY)
  const methodOn = (id) => entry.methods?.[id]?.enabled !== false
  const connectable = accountFields(provider).length > 0
  const connected = isConnected(provider, entry)
  // how each way on is priced: on the chip's hint (the row stays one line)
  const plans = provider.methods.filter((method) => methodOn(method.id)).map((method) => ({ method, ...wayPlan(t, provider, method, entry.methods?.[method.id], connected) }))
  const ownPrices = plans.some((plan) => plan.own)
  const onCount = provider.methods.filter((method) => methodOn(method.id)).length
  // taken off by Aldoo: off whatever the company left on, its switch locked
  const aldooOff = platformOff(provider)
  const endsOn = platformEnds(provider)
  const off = entry.enabled === false || aldooOff
  return (
    <div className="relative flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`shipping-provider-${provider.id}`}>
      {/* on or off, first on the row */}
      <span className={!canEdit ? "opacity-50 pointer-events-none" : ""} data-testid={`shipping-provider-on-${provider.id}`}>
        <ToggleSwitch variant="admin" isOn={!off && !blocked} onChange={(enabled) => canEdit && !aldooOff && (blocked && enabled ? onBlocked?.() : onChange({ ...entry, enabled: !!enabled }, { now: true }))} />
      </span>

      <ProviderLogo provider={provider} off={off} className="w-16 h-9 shrink-0 rounded" />

      <button 
        type="button" 
        onClick={onOpen} 
        className={`min-w-0 text-left ${off ? "opacity-60" : ""}`}
      >
        <div className="font-semibold text-admin_text">
          {provider.name}
        </div>
        <div className="text-xs text-admin_text_secondary">
          {t("%s of %s delivery ways on", onCount, provider.methods.length)} · {countriesText(provider.countries, t)}
        </div>
      </button>
      {/* the company's own account with the courier: Connect, or connected */}
      {connectable &&
        (connected ? (
          <button type="button" 
            onClick={onOpen} 
            className="inline-flex items-center gap-1 rounded-full bg-admin_success/10 text-admin_success text-xs font-semibold px-2.5 py-1" 
            data-testid={`shipping-provider-connected-${provider.id}`}
          >
            <CheckCircleIcon className="w-4 h-4" />
            {t`Connected`}
          </button>
        ) : (
          canEdit && (
            <Button 
              variant="admin_secondary" 
              onClick={onConnect} 
              data-testid={`shipping-provider-connect-${provider.id}`}
            >
              <span className="flex items-center gap-1.5">
                <LinkIcon className="w-4 h-4" />
                {t`Connect`}
              </span>
            </Button>
          )
        ))}
      {aldooOff && (
        <Chip className="bg-admin_error/10 text-admin_error" data-testid={`shipping-provider-aldoo-off-${provider.id}`}>
          {provider.availability?.disableAt ? t("Disabled by Aldoo on %s", new Date(provider.availability.disableAt).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })) : t`Disabled by Aldoo`}
        </Chip>
      )}
      {endsOn && (
        <Chip className="bg-admin_warning/15 text-admin_warning" data-testid={`shipping-provider-ends-${provider.id}`}>{t("Ends on %s", new Date(endsOn).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }))}</Chip>
      )}
      {/* the prices and Settings: one group at the right, wrapping to the next line together; on a phone the card's full width */}
      <div className="flex items-center gap-3 ml-auto max-sm:mt-3 max-sm:w-full">
        {/* the prices and their Settings side by side, no frame; on a phone one above the other, the card's width */}
        <div className="flex max-sm:flex-col max-sm:w-full items-center gap-3 max-sm:gap-2">
          <span title={plans.map((plan) => `${t(plan.method.label)}: ${plan.text}`).join("\n")}>
            <Chip className={ownPrices ? "bg-admin_success/10 text-admin_success" : "bg-admin_neutral_1 text-admin_text_secondary"}>
              {ownPrices ? t`Own prices` : connected ? t("%s prices", provider.name) : t`Platform prices`}
              <CheckIcon className="w-3.5 h-3.5 ml-1 shrink-0" strokeWidth={2.5} aria-hidden="true" />
            </Chip>
          </span>
          <Button 
            variant="admin_secondary" 
            onClick={onOpen} 
            className="max-sm:w-full"
            data-testid={`shipping-provider-settings-${provider.id}`}
          >
            <span className="flex items-center gap-1.5">
              <Cog6ToothIcon className="w-4 h-4" />
              {t`Settings`}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * A provider's settings, in a dialog of its own: the ways it delivers (each on or off) and,
 * when the provider takes one, the company's account with it and the sender — a tab each.
 * Every change saves as it is made, like the rest of the page.
 */
/**
 * What a buyer pays to collect the order at the company's shop: free (nothing stored), or the company's price
 * (one line of its own prices, for any weight). Shown either way, so the vendor sees what buyers see.
 */
function ShopPickupPrice({ way, canEdit, onChange }) {
  const t = usePortalTexts(COMPANY)
  const saved = Number(way?.prices?.[0]?.price) || 0
  const [custom, setCustom] = useState(saved > 0)
  const [text, setText] = useState(saved > 0 ? String(saved) : "")
  const value = Number(String(text).replace(",", "."))
  const wrong = custom && text !== "" && !(value > 0 && value <= 10000)
  const choose = (paid) => {
    if (!canEdit) return
    setCustom(paid)
    if (!paid) return onChange({ prices: undefined }, { now: true })
    if (value > 0) onChange({ prices: [{ upToKg: 100, price: Math.round(value * 100) / 100 }] }, { now: true })
  }
  const save = () => {
    if (!custom || !(value > 0 && value <= 10000)) return
    onChange({ prices: [{ upToKg: 100, price: Math.round(value * 100) / 100 }] }, { now: true })
  }
  const chip = (on) => `flex-1 h-9 rounded-full border text-sm font-semibold ${on ? "bg-admin_primary text-white border-admin_primary" : "border-admin_bordered text-admin_text hover:border-admin_primary"} ${canEdit ? "" : "opacity-50 cursor-not-allowed"}`
  return (
    <div className="flex flex-col gap-2 pt-1" data-testid="shop-pickup-price">
      <span className="text-xs font-semibold text-admin_text">{t`Price for the buyer`}</span>
      <div className="flex gap-2">
        <button type="button" className={chip(!custom)} onClick={() => choose(false)} data-testid="shop-pickup-free">{t`Free`}</button>
        <button type="button" className={chip(custom)} onClick={() => choose(true)} data-testid="shop-pickup-paid">{t`Your price`}</button>
      </div>
      {custom ? (
        <Field error={wrong ? t`A price above zero, e.g. 2.00` : null}>
          <span className="flex items-center gap-2">
            <span className="text-sm text-admin_text_secondary">€</span>
            <TextInput value={text} onChange={setText} onBlur={save} inputMode="decimal" placeholder="2.00" disabled={!canEdit} data-testid="shop-pickup-price-input" />
          </span>
        </Field>
      ) : (
        <span className="text-xs text-admin_text_secondary">{t`Buyers collect the order at your shop for free.`}</span>
      )}
    </div>
  )
}

// where a courier delivers, in a word: its few countries, else how many
const countriesText = (countries, t) => (!countries?.length ? "" : countries.length > 4 ? t("%s countries", countries.length) : countries.join(", "))

// where the company sends with one courier (shipping-providers areaOf): its own area by default
const AREA_MODES = [
  { value: "company", label: "Same as your delivery settings" },
  { value: "worldwide", label: "Worldwide" },
  { value: "abroad", label: "Abroad only (outside Bulgaria)" },
  { value: "countries", label: "Selected countries only" },
]

/**
 * A courier's own shipping area and delivery time (the Shipping area tab): the checkout offers the courier
 * only for an address in its area — two couriers for Bulgaria and a third for abroad — and the expected
 * delivery follows its days. Unset, both are the company's (Delivery settings: Ships to, delivery days).
 */
function ShippingAreaTab({ entry, companyArea, canEdit, onChange, onFlush }) {
  const t = usePortalTexts(COMPANY)
  const area = entry.shipsTo?.mode ? entry.shipsTo : { mode: "company", countries: [] }
  const company = companyArea?.shipsTo?.mode === "countries" && companyArea.shipsTo.countries?.length ? companyArea.shipsTo.countries.map(countryName).join(", ") : t`Worldwide`
  const setArea = (patch) => onChange({ ...entry, shipsTo: { ...area, ...patch } }, { now: true })
  const days = entry.deliveryDays ?? ""
  const modes = AREA_MODES.map((mode) => ({ ...mode, label: t(mode.label) }))
  return (
    <div className="flex flex-col gap-4" data-testid="shipping-provider-area">
      <Grid cols={2}>
        <Field label={t`Ships to`} hint={area.mode === "company" ? t("Your delivery settings: %s", company) : t`Buyers with an address elsewhere do not see this courier.`}>
          <div data-testid="provider-area-mode">
            <DropdownSelect
              variant="admin"
              className="w-full"
              options={modes}
              value={modes.filter((mode) => mode.value === area.mode)}
              nameField="label"
              showCheckbox={false}
              showSelectionTick
              enableResetButton={false}
              allowEmptySelection={false}
              disabled={!canEdit}
              onChange={(sel) => {
                const picked = Array.isArray(sel) ? sel[0] : sel
                if (picked?.value && picked.value !== area.mode) setArea({ mode: picked.value })
              }}
            />
          </div>
        </Field>
        {area.mode === "countries" && (
          <Field label={t`Countries`} hint={t`Until one is picked, this courier is offered nowhere.`}>
            <CountriesSelect value={area.countries || []} placeholder={t`Pick countries…`} onChange={(countries) => setArea({ countries })} disabled={!canEdit} data-testid="provider-area-countries" />
          </Field>
        )}
      </Grid>
      <Grid cols={2}>
        <Field label={t`Delivery time (business days)`} hint={companyArea?.deliveryDays ? t("Empty: your default, %s days. The expected delivery at checkout follows it.", companyArea.deliveryDays) : t`Empty: your default delivery days. The expected delivery at checkout follows it.`}>
          <TextInput
            type="number"
            min={1}
            max={60}
            inputMode="numeric"
            value={days}
            placeholder={companyArea?.deliveryDays ? String(companyArea.deliveryDays) : ""}
            onChange={(value) => onChange({ ...entry, deliveryDays: value === "" ? null : Math.max(1, Math.min(60, Math.round(Number(value)) || 1)) })}
            onBlur={onFlush}
            disabled={!canEdit}
            data-testid="provider-delivery-days"
          />
        </Field>
      </Grid>
    </div>
  )
}

function ProviderDialog({ orgId, provider, entry, companyArea, canEdit, onChange, onFlush, onClose, onRemove, onConnect, onDisconnect }) {
  const t = usePortalTexts(COMPANY)
  const hasAccount = provider.configFields.length > 0
  const connectable = accountFields(provider).length > 0
  const connected = isConnected(provider, entry)
  const sender = senderFields(provider)
  const dialogTabs = [
    { value: "ways", label: t`Delivery ways` },
    ...(connectable ? [{ value: "account", label: t`Account` }] : []),
    ...(sender.length ? [{ value: "sender", label: t`Sender` }] : []),
    // the shop's pickup is the shop's own place: no area of its own
    ...(provider.id !== "shop" ? [{ value: "area", label: t`Shipping area` }] : []),
  ]
  const [tab, setTab] = useState("ways")
  const methodOn = (id) => entry.methods?.[id]?.enabled !== false
  const setMethod = (id, enabled) => onChange({ ...entry, methods: { ...entry.methods, [id]: { ...entry.methods?.[id], enabled } } }, { now: true })
  // a way's own price lists (`prices` for anywhere, `locations` for towns); a key set to undefined is
  // dropped (the provider's prices again, or no towns of its own)
  const setWay = (id, patch, options) => {
    const next = { ...entry.methods?.[id], enabled: methodOn(id) }
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) delete next[key]
      else next[key] = value
    }
    onChange({ ...entry, methods: { ...entry.methods, [id]: next } }, options)
  }
  // the towns to price on their own (the couriers' office towns), asked once the dialog opens
  const [towns, setTowns] = useState([])
  useEffect(() => {
    let alive = true
    ShippingTowns(null, 100).then((res) => alive && Array.isArray(res?.towns) && setTowns(res.towns)).catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  const setSetting = (key, value) => onChange({ ...entry, settings: { ...entry.settings, [key]: value } })
  const close = () => {
    onFlush?.()
    onClose()
  }
  return (
    <Modal
      isOpen
      onClose={close}
      variant="admin"
      closeOnEscape
      // the shop's one way: a narrow dialog, not the couriers' three cards wide
      styleOverrides={{ container: `${provider.id === "shop" ? "w-[min(96vw,560px)]" : "w-[min(96vw,1080px)]"} max-h-full md:max-h-[calc(100vh-2rem)] bg-admin_paper rounded-lg border border-admin_bordered shadow-lg overflow-hidden` }}
    >
      <div className="flex flex-col gap-4 p-5 pt-1" data-testid={`shipping-provider-dialog-${provider.id}`}>
        <div className="flex items-center gap-3">
          <ProviderLogo provider={provider} className="w-20 h-11 shrink-0 rounded" />
          <div className="min-w-0">
            {/* the help center on the ways and prices, the account, the sender (vendor.delivery): beside the name */}
            <div className="flex items-center gap-1.5 text-lg font-semibold text-admin_text">
              {provider.name}
              <HelpHint helpKey={tab === "ways" ? "vendor.delivery#ways" : "vendor.delivery#account"} />
            </div>
            <div className="text-xs text-admin_text_secondary">{countriesText(provider.countries, t)}</div>
          </div>
        </div>
        {/* a phone: the tabs as a dropdown (its list under it, not a sheet over this dialog); from sm the tabs */}
        {hasAccount && (
          <>
            <SectionPicker tabs={dialogTabs} value={tab} onChange={setTab} sheet={false} from="sm" testId="shipping-provider-tabs-select" />
            <Tabs variant="admin" value={tab} onChange={setTab} tabs={dialogTabs} className="hidden sm:block" />
          </>
        )}
        {/* one height for both tabs: switching does not resize the dialog */}
        <div className={hasAccount ? "min-h-[320px]" : ""}>
        {tab === "ways" && (
          <div className={`grid grid-cols-1 ${provider.id === "shop" ? "" : "md:grid-cols-3"} gap-3 ${entry.enabled === false ? "opacity-50" : ""}`}>
            {provider.methods.map((method) => {
              const Icon = METHOD_ICONS[method.id] || TruckIcon
              const on = methodOn(method.id)
              return (
                <div key={method.id} className={`relative flex flex-col gap-2 rounded-lg border p-3 ${on ? "border-admin_primary/40 bg-admin_primary/5" : "border-admin_bordered"}`} data-testid={`shipping-method-${method.id}`}>
                  {/* room on the right for the prices' reset button */}
                  <div className="flex items-center gap-2 pr-8">
                    <span className={!canEdit || entry.enabled === false ? "opacity-50 pointer-events-none" : ""}>
                      <ToggleSwitch variant="admin" isOn={on} onChange={(enabled) => canEdit && entry.enabled !== false && setMethod(method.id, !!enabled)} />
                    </span>
                    <Icon className={`w-5 h-5 ${on ? "text-admin_primary" : "text-admin_text_secondary"}`} />
                    {/* the way in a word (its limit beside it): the cards' price controls line up */}
                    <span className="flex-1 text-sm font-semibold text-admin_text" title={t(method.detail)}>
                      {method.maxWeightGrams ? t("%s (%s kg)", t(WAY_NAMES[method.id] || method.label), method.maxWeightGrams / 1000) : t(WAY_NAMES[method.id] || method.label)}
                    </span>
                  </div>
                  {provider.id === "shop" ? (
                    <ShopPickupPrice way={entry.methods?.[method.id]} canEdit={canEdit && entry.enabled !== false} onChange={(patch, options) => setWay(method.id, patch, options)} />
                  ) : (
                    <MethodPrices method={method} way={entry.methods?.[method.id]} towns={towns} canEdit={canEdit && entry.enabled !== false} onChange={(patch, options) => setWay(method.id, patch, options)} onFlush={onFlush} />
                  )}
                </div>
              )
            })}
          </div>
        )}
        {/* the company's own account with the courier: connected (or not) in the Connect dialog, where it is checked */}
        {tab === "account" && connectable && (
          <div className="flex flex-col gap-4" data-testid="shipping-provider-account">
            {/* on a phone the words above the buttons (the buttons the card's width); from sm side by side */}
            <div className="rounded-lg border border-admin_bordered px-4 py-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
              <div className="flex-1 min-w-0 flex items-center gap-3">
              {connected ? <CheckCircleIcon className="w-6 h-6 text-admin_success shrink-0" /> : <LinkIcon className="w-6 h-6 text-admin_text_secondary shrink-0" />}
              <div className="flex-1 min-w-0 flex flex-col">
                <span className="text-sm font-semibold text-admin_text">{connected ? t("Connected to your %s account", provider.name) : t("Not connected to %s", provider.name)}</span>
                <span className="text-xs text-admin_text_secondary">
                  {connected ? t("API user %s · waybills are made with your account, at your prices", entry.settings?.userName || ((key) => (key ? `${String(key).slice(0, 6)}…` : ""))(entry.settings?.apiKey || entry.settings?.oauthClientId)) : t("Without your account, delivery is priced by the platform's price list and waybills are made in %s by hand.", provider.name)}
                </span>
              </div>
              </div>
              {canEdit && (
                <span className="flex items-center gap-2 max-sm:[&>*]:flex-1">
                  {connected && (
                    <Button variant="admin_text" onClick={onDisconnect} className="text-admin_error" data-testid="shipping-provider-disconnect">
                      {t`Disconnect`}
                    </Button>
                  )}
                  <Button variant={connected ? "admin_secondary" : "admin_primary"} onClick={onConnect} data-testid="shipping-provider-account-connect">
                    {connected ? t`Change account` : t`Connect`}
                  </Button>
                </span>
              )}
            </div>
            {!connected && <ConnectGuide provider={provider} />}
          </div>
        )}
        {tab === "area" && <ShippingAreaTab entry={entry} companyArea={companyArea} canEdit={canEdit} onChange={onChange} onFlush={onFlush} />}
        {tab === "sender" && sender.length > 0 && (
          <Grid cols={2}>
            {sender.map((field) => {
              const setNow = (v) => onChange({ ...entry, settings: { ...entry.settings, [field.key]: v } }, { now: true })
              if (isServicePick(provider, field)) return <ServiceField key={field.key} orgId={orgId} provider={provider} field={field} value={entry.settings?.[field.key]} connected={connected} canEdit={canEdit} onPick={setNow} />
              if (isSenderPick(provider, field))
                return <SenderField key={field.key} orgId={orgId} provider={provider} field={field} value={entry.settings?.[field.key]} connected={connected} canEdit={canEdit} onPick={(id) => onChange({ ...entry, settings: { ...entry.settings, [field.key]: id } }, { now: true })} onConnect={onConnect} />
              const wrong = settingFails(field, entry.settings?.[field.key])
              const error = wrong ? (field.format === "digits" ? t`Digits only` : t`A whole number above zero`) : null
              return (
                <Field key={field.key} label={t(field.label)} hint={field.hint ? t(field.hint) : field.hint} error={error}>
                  <TextInput
                    type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
                    inputMode={field.format ? "numeric" : undefined}
                    min={field.format === "positive_integer" ? 1 : undefined}
                    value={entry.settings?.[field.key] ?? ""}
                    placeholder={field.placeholder || ""}
                    autoComplete={field.type === "password" ? "new-password" : "off"}
                    onChange={(value) => setSetting(field.key, value)}
                    onBlur={onFlush}
                    aria-invalid={wrong ? true : undefined}
                    disabled={!canEdit}
                    data-testid={`shipping-setting-${field.key}`}
                  />
                </Field>
              )
            })}
          </Grid>
        )}
        </div>
        <div className="flex items-center justify-between gap-2">
          {canEdit && onRemove ? (
            <Button variant="admin_secondary" onClick={onRemove} className="!text-admin_error !border-admin_error/40 hover:!bg-admin_error/10" data-testid="shipping-provider-dialog-remove">
              <span className="flex items-center gap-1.5">
                <TrashIcon className="w-4 h-4" />
                {t`Remove delivery method`}
              </span>
            </Button>
          ) : (
            <span />
          )}
          <Button variant="admin_primary" onClick={close} data-testid="shipping-provider-dialog-done">
            {t`Done`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * `value` is the company's list as the API sends it (empty until it adds a
 * courier, secrets masked); `onChange(list, options)` saves it the way the other
 * settings are saved.
 */
// `shopsCount`: the company's open shops (Delivery → My shops) — pickup from the shop goes nowhere without one
export default function ShippingProvidersSection({ orgId, value, catalog = [], canEdit, onChange, onFlush, error = null, shopsCount = 0, onOpenShops, companyArea = null }) {
  const t = usePortalTexts(COMPANY)
  const list = Array.isArray(value) ? value : []
  // the provider whose settings are open (its id)
  const [openId, setOpenId] = useState(null)
  // the provider whose Connect dialog is open (its id)
  const [connectId, setConnectId] = useState(null)
  // the account as the API saved it (checked with the courier): that provider's settings taken into the list
  const accountSaved = (providerId, providers) => {
    const saved = (providers || []).find((entry) => entry.providerId === providerId)
    if (!saved) return
    const has = list.some((entry) => entry.providerId === providerId)
    const next = has ? list.map((entry) => (entry.providerId === providerId ? { ...entry, settings: saved.settings } : entry)) : [...list, saved]
    onChange(next, { now: true })
  }
  const disconnect = (provider) =>
    showPromptDialog({
      title: t("Disconnect your %s account?", provider.name),
      message: t("Waybills are no longer made with your account from the order, and delivery is priced by the platform's price list. You can connect it again at any time."),
      confirmText: t`Disconnect`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        const res = await ConnectCourier(orgId, provider.id, { disconnect: true }).catch(() => null)
        if (res?.result) accountSaved(provider.id, res.providers)
      },
    })
  const known = (id) => catalog.find((provider) => provider.id === id)
  // pickup from the shop goes nowhere without a shop: added and switched on once there is one open
  const needShops = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Add a shop first`,
      message: t`Pickup from the shop is offered once you have a shop open for buyers to collect from. Add your shops in My shops, then switch it on.`,
      confirmText: t`Add a shop`,
      cancelText: t`Cancel`,
      onConfirm: () => onOpenShops?.(),
    })
  // pickup from the shop delivers only with a shop open
  const delivers = (l) => deliversWith(catalog)((Array.isArray(l) ? l : []).filter((entry) => entry?.providerId !== "shop" || shopsCount > 0))
  // a courier Aldoo takes off (on a date, or now) is not one to add
  const addable = catalog.filter((provider) => !list.some((entry) => entry.providerId === provider.id) && (provider.availability?.status || "active") === "active")
  // a change that leaves no courier delivering pauses the physical products on
  // the store until one is added: it is asked first (the last one removed, or
  // turned off, or its last way turned off)
  const stopsDelivering = (next) => delivers(list) && !delivers(next)
  const confirmPause = ({ title, confirmText, onConfirm }) =>
    showPromptDialog({
      title,
      message: t`Your physical products will be paused on the store — not listed, found or sold — until you add a shipping provider. Digital products are not affected.`,
      confirmText,
      cancelText: t`Cancel`,
      onConfirm,
    })
  const replace = (index, entry, options) => {
    const next = list.map((item, i) => (i === index ? entry : item))
    if (!stopsDelivering(next)) return onChange(next, options)
    confirmPause({ title: t`Turn off the last shipping provider?`, confirmText: t`Pause physical sales`, onConfirm: () => onChange(next, { ...options, now: true }) })
  }

  const remove = (index) => {
    const provider = known(list[index].providerId)
    const next = list.filter((_, i) => i !== index)
    const done = () => {
      setOpenId(null)
      onChange(next, { now: true })
    }
    if (stopsDelivering(next)) return confirmPause({ title: t`Remove the last shipping provider?`, confirmText: t`Pause physical sales`, onConfirm: done })
    showPromptDialog({
      title: t("Remove %s?", provider?.name || t`provider`),
      message: t`Buyers will no longer be offered its delivery ways for your products. You can add it again later.`,
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: done,
    })
  }

  return (
    <Section
      title={<span className="inline-flex items-center gap-1.5">{t`Shipping providers`}<HelpHint helpKey="vendor.delivery#providers" /></span>}
      // the heading and Add provider side by side from sm; on a phone one above the other, the button at the left
      headerClassName="items-start max-sm:mb-3 sm:flex-row sm:items-center sm:justify-between"
      subtitle={t`The couriers your physical products ship with, and the ways each one delivers. Checkout offers the ways that are on.`}
      actions={
        canEdit &&
        (addable.length ? (
          // the platform's couriers not added yet, searched by name
          <DropdownSelect
            options={addable.map((provider) => ({ value: provider.id, name: provider.name, provider }))}
            value={[]}
            onChange={(selection) => {
              const picked = (Array.isArray(selection) ? selection[0] : selection)?.provider
              if (picked?.id === "shop" && !shopsCount) return needShops()
              if (picked) onChange([...list, entryOf(picked)], { now: true })
            }}
            searchEnabled
            placeholder={t`Search providers`}
            showCheckbox={false}
            enableResetButton={false}
            allowEmptySelection={false}
            variant="admin"
            className="!p-0 !m-0 !w-auto"
            dropdownWidth="16rem"
            customTrigger={
              <span className="h-[40px] flex items-center justify-center gap-1.5 border border-admin_primary text-admin_primary hover:text-white hover:bg-admin_primary font-bold rounded-full whitespace-nowrap py-2 px-4 transition-colors" data-testid="add-shipping-provider">
                <PlusIcon className="w-4 h-4" />
                {t`Add provider`}
              </span>
            }
            onItemRender={(option) => (
              <span className="flex items-center gap-2 py-1 text-sm text-admin_text">
                <ProviderLogo provider={option.provider} className="w-10 h-6 shrink-0 rounded-sm" />
                {option.name}
              </span>
            )}
          />
        ) : (
          <Button variant="admin_secondary" disabled data-testid="add-shipping-provider">
            <span className="flex items-center gap-1.5">
              <PlusIcon className="w-4 h-4" />
              {t`Add provider`}
            </span>
          </Button>
        ))
      }
    >
      <div className="flex flex-col gap-3">
        {!list.length && <Empty>{t`No shipping provider: your physical products are paused on the store until you add one.`}</Empty>}
        {list.length > 0 && !delivers(list) && (
          <p className="m-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="shipping-providers-paused">
            {t`No shipping provider is on: your physical products are paused on the store until you turn one on.`}
          </p>
        )}
        {/* a setting in the wrong format: the couriers wait to be saved until it is right */}
        {error && <p className="m-0 text-xs text-admin_error" role="alert" data-testid="shipping-providers-error">{error}</p>}
        {list.length > 0 && (
          <div className="rounded-xl border border-admin_bordered bg-admin_paper divide-y divide-admin_bordered">
            {list.map((entry, index) => {
              const provider = known(entry.providerId)
              if (!provider) return null
              return (
                <React.Fragment key={entry.providerId}>
                <ProviderRow
                  provider={provider}
                  entry={entry}
                  canEdit={canEdit}
                  onChange={(next, options) => replace(index, next, options)}
                  onOpen={() => setOpenId(entry.providerId)}
                  onConnect={() => setConnectId(entry.providerId)}
                  blocked={entry.providerId === "shop" && !shopsCount}
                  onBlocked={needShops}
                />
                {entry.providerId === "shop" && !shopsCount && (
                  <div className="-mt-1 rounded-md bg-admin_warning/15 px-4 py-2 text-xs text-admin_text" data-testid="shop-provider-needs-shop">
                    {t`Buyers cannot pick up from your shop until you add one in My shops.`}{" "}
                    {onOpenShops && <button type="button" className="text-admin_primary underline" onClick={onOpenShops}>{t`Add a shop`}</button>}
                  </div>
                )}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>
      {(() => {
        const index = list.findIndex((entry) => entry.providerId === openId)
        const provider = index >= 0 ? known(openId) : null
        if (!provider) return null
        return (
          <ProviderDialog
            orgId={orgId}
            provider={provider}
            entry={list[index]}
            companyArea={companyArea}
            canEdit={canEdit}
            onChange={(next, options) => replace(index, next, options)}
            onFlush={onFlush}
            onClose={() => setOpenId(null)}
            onRemove={() => remove(index)}
            onConnect={() => setConnectId(provider.id)}
            onDisconnect={() => disconnect(provider)}
          />
        )
      })()}
      {connectId && known(connectId) && (
        <ConnectDialog
          orgId={orgId}
          provider={known(connectId)}
          sender={list.find((entry) => entry.providerId === connectId)?.settings?.clientId}
          onClose={() => setConnectId(null)}
          onConnected={(providers) => accountSaved(connectId, providers)}
          onSender={(id) => onChange(list.map((entry) => (entry.providerId === connectId ? { ...entry, settings: { ...entry.settings, clientId: id } } : entry)), { now: true })}
        />
      )}
    </Section>
  )
}
